import { MetabaseEmbedPayload, ProjectsService } from '@/api';
import Breadcrumb from '@/components/breadcrumb';
import Modal from '@/components/report-error-modal';
import { useTheme } from '@/components/theme-provider';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { PencilLine, X } from 'lucide-react';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { useStore } from '@nanostores/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ColumnDef,
  PaginationState,
  SortingState,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import 'ag-grid-community/styles/ag-grid.css'; // Mandatory CSS required by the grid
import 'ag-grid-community/styles/ag-theme-quartz.min.css'; // Optional Theme applied to the grid
import * as jose from 'jose';
import {
  AlertCircle,
  ArrowUpDown,
  ChevronFirst,
  ChevronLast,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Search,
  Sparkles,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import Markdown from 'react-markdown';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';

export type ProjectPageProps = {
  workspaceId: string;
  projectId: string;
};

interface MarkdownTextProps {
  text: string;
}

export const ProjectPage = ({ workspaceId, projectId }: ProjectPageProps) => {
  const [isOpen, setOpen] = useState(false);
  // get the current workspace

  const ws = useStore($workspace);
  // get the current organization

  const org = useStore($org);
  // get the current project
  const pr = useStore($project);
  const sidebarCollapsed = useStore($sideBarCollapsed);
  // breadcrumb pages
  const queryClient = useQueryClient();

  const pages = [
    {
      label: org?.name || 'Organizations',
      href: org?.name
        ? `/organization/${org?.id}/workspaces`
        : '/organizations',
    },
    {
      label: ws?.workspace?.name || 'Workspaces',
      href: ws?.workspace?.name
        ? `/organization/${org?.id}/workspace/${ws?.workspace.id}/projects`
        : `/organization/${org?.id}/workspaces`,
    },
    {
      label: 'Project',
      href: `/organization/${org?.id}/workspace/${ws?.workspace.id}/projects`,
    },
    {
      label: pr?.name || 'Project',
      href: `/organization/${org?.id}/workspace/${ws?.workspace.id}/projects/${projectId}`,
    },
  ];

  const { theme } = useTheme();
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 1,
    pageSize: 10,
  });
  const [metabaseUrl, setMetabaseUrl] = useState<undefined | string>(undefined);
  const [visibleColumns, setVisibleColumns] = useState<string[]>([]);
  const [isColumnDropdownOpen, setColumnDropdownOpen] = useState(false);
  const [isDownloadDropdownOpen, setDownloadDropdownOpen] = useState(false);
  const [expandedCells, setExpandedCells] = useState<{
    [key: string]: boolean;
  }>({});

  const [analyzingCsv, setAnalyzingCsv] = useState(false);
  const [
    smartAnalyzeCsvAnalyzationResult,
    setSmartAnalyzeCsvAnalyzationResult,
  ] = useState<string | undefined | null>('');
  const smartAnalyzeCsvAnalyzationRef = useRef<HTMLDivElement | null>(null);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');

  const [summary, setSummary] = useState({
    totalRows: 0,
    legalActionThreatenedCount: 0,
    potentialUDAAPComplaintsCount: 0,
    potentialCFPBComplaintsCount: 0,
  });
  // get the project result
  const [metaColumns, setMetaColumns] = useState<
    { name: string; editing: boolean }[]
  >([]);

  const projectResult = useQuery({
    queryKey: ['getProjectResult', workspaceId, projectId],
    queryFn: async () => {
      const result = await ProjectsService.getResults(
        projectId,
        true,
        pagination?.pageIndex, // page number
        pagination?.pageSize, // limit or page size
        sorting[0]?.id, // sort column
        sorting[0]?.desc ? 'desc' : 'asc', // sort order
        searchTerm,
      );

      if (!result) {
        throw new Error('No data found');
      }

      if (result?.rows !== null && result?.rows?.length > 0) {
        const metaCols = result?.rows[0]?.metaColumns?.map((meta: any) => ({
          name: meta,
          editing: false,
        }));
        setMetaColumns(metaCols);
        result.rows = result?.rows.map((item: any) => {
          delete item.metaColumns;
          return item;
        });
      }

      // Check if all `Meta` arrays are empty
      const allMetaEmpty = result?.rows?.every(
        (item: any) => Array.isArray(item?.Meta) && item?.Meta?.length === 0,
      );

      result.rows = result?.rows?.map((item: any) => {
        if (allMetaEmpty) {
          // If all `Meta` arrays are empty, remove the `Meta` key
          const { Meta, ...rest } = item;
          return rest;
        } else {
          const { Meta } = item;
          const obj = item;
          Meta?.forEach((meta: any, m: number) => {
            obj[`Meta ${m + 1}`] = meta;
          });
          delete obj.Meta;
          return obj;
        }
      });

      // retrieve the columns
      let columns: string[] = [];
      if (result?.rows.length !== 0) {
        columns = Object.keys(result?.rows[0]);
      }

      const totalRows = result?.totalCount;

      const legalActionThreatenedCount = result.rows.filter(
        (row: any) => row['Legal Action'] && row['Legal Action'] !== 'No',
      ).length;

      const potentialUDAAPComplaintsCount = result.rows.filter(
        (row: any) =>
          row['Potential UDAAP Complaint'] &&
          row['Potential UDAAP Complaint'] === 'Yes',
      ).length;

      const potentialCFPBComplaintsCount = result.rows.filter(
        (row: any) =>
          row['Potential CFPB Complaint'] &&
          row['Potential CFPB Complaint'] === 'Complaint',
      ).length;

      setSummary({
        totalRows,
        legalActionThreatenedCount,
        potentialUDAAPComplaintsCount,
        potentialCFPBComplaintsCount,
      });
      setVisibleColumns(columns);

      return { data: result?.rows, columns };
    },
  });

  const columnHelper = createColumnHelper<any>();
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentRowId, setCurrentRowId] = useState<string | null>(null);
  const [currentColumnId, setCurrentColumnId] = useState<string | null>(null);
  const [csvReportRowId, setCsvReportRowId] = useState<string | null>(null);
  const [originalValue, setOriginalValue] = useState<string | null>(null); // New state for original value
  const [message, setMessage] = useState<string | null>(null); // State for feedback message

  const handleReport = async (
    rowId: string,
    columnId: string,
    originalValue: string,
    newValue: string,
  ) => {
    try {
      console.log(
        `Reported original value: ${originalValue}, New value: ${newValue} for Row ID: ${rowId}, Column ID: ${columnId}, CSV Report Row ID: ${csvReportRowId}, orgId: ${org?.id}, projectId: ${projectId}`,
      );
      const result = await ProjectsService.reportError(
        projectId,
        rowId,
        columnId,
        originalValue,
        newValue,
        org?.id,
      );
      if (result.isSuccess) {
        setMessage('Report submitted successfully!');
      }
    } catch (error) {
      console.error('Error reporting:', error);
      setMessage('Failed to submit report. Please try again.'); // Error message
    }
  };

  const columns: ColumnDef<any>[] = useMemo(
    () =>
      projectResult?.data?.columns
        ?.filter(col => visibleColumns.includes(col))
        .map(col => ({
          accessorKey: col,
          header: ({ column }) => {
            // console.log('head', col);
            return (
              <div
                className={`flex ${col === 'ID' ? 'min-w-[50px]' : 'min-w-[200px]'} items-center justify-start uppercase`}
              >
                {col}
                {col === 'Date' && (
                  <span
                    onClick={() =>
                      column.toggleSorting(column.getIsSorted() === 'asc')
                    }
                    className="ml-1 cursor-pointer p-[2px] hover:rounded-md hover:bg-slate-200"
                  >
                    <ArrowUpDown className="h-[14px] w-4 " />
                  </span>
                )}

                {/* <Select
                  onValueChange={value => column.setFilterValue(value)}
                  value={(column.getFilterValue() as string) ?? ''}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Filter..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">All</SelectItem>
                    {getUniqueValues(col).map(value => (
                      <SelectItem key={value} value={value}>
                        {value}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select> */}
              </div>
            );
          },
          cell: ({ row, column }) => {
            const value = row.getValue(column.id) as string;
            const isExpanded = expandedRows[`${row.id}-${column.id}`];
            const toggleExpand = () => {
              setExpandedRows(prev => ({
                ...prev,
                [`${row.id}-${column.id}`]: !prev[`${row.id}-${column.id}`],
              }));
            };
            const csvReportRowIdValue = row.getValue(
              projectResult?.data?.columns[0],
            ) as string;
            const columnIndex = projectResult?.data?.columns.indexOf(column.id);

            // Specific check for 'Context' column
            if (column.id === 'Context') {
              const formattedValue = value.replace(/\n/g, '<br/>');
              return (
                <div
                  className={cn(
                    'py-auto px-1.5 text-sm',
                    'white-space: nowrap px-auto w-[500px]',
                  )}
                >
                  <div
                    style={{
                      wordBreak: 'break-word', // Break long words
                      overflowWrap: 'break-word', // Wrap content to the next line
                      whiteSpace: 'normal', // Ensure normal wrapping
                    }}
                    dangerouslySetInnerHTML={{
                      __html: isExpanded
                        ? formattedValue
                        : `${formattedValue.slice(0, 50)}...`,
                    }}
                  />
                  <Button
                    variant="link"
                    onClick={toggleExpand}
                    className="h-auto p-0 font-normal"
                  >
                    {isExpanded ? 'Read Less' : 'Read More'}
                  </Button>
                </div>
              );
            }

            // General check for long strings
            const isLongString = typeof value === 'string' && value.length > 50;

            if (columnIndex <= 2) {
              return <div>{value} </div>;
            }

            return (
              <div
                className={
                  isLongString
                    ? cn(
                      'py-auto px-1.5 text-sm',
                      'white-space: nowrap px-auto w-[500px]',
                    )
                    : ''
                }
              >
                {isLongString
                  ? isExpanded
                    ? value
                    : `${value.slice(0, 50)}...`
                  : value}
                {isLongString && (
                  <Button
                    variant="link"
                    onClick={toggleExpand}
                    className="h-auto p-0 font-normal"
                  >
                    {isExpanded ? 'Read Less' : 'Read More'}
                  </Button>
                )}
                {columnIndex > 2 && column.id !== 'Areas of Improvement' && (
                  <Button
                    variant="link"
                    onClick={() => {
                      setCurrentRowId(row.id); // Set the current row ID
                      setCurrentColumnId(column.id); // Set the current column ID
                      setCsvReportRowId(csvReportRowIdValue); // Set the CSV report row ID
                      setOriginalValue(value); // Set the original value
                      setIsModalOpen(true); // Open modal
                    }}
                    className="ml-2 inline-flex items-center" // Add inline-flex and items-center
                  >
                    <AlertCircle className="h-4 w-4" />
                  </Button>
                )}
              </div>
            );
          },
        })) || [],
    [projectResult?.data?.columns, visibleColumns, expandedRows],
  );

  const table = useReactTable({
    data: projectResult?.data?.data || [],
    columns: columns || [],
    getCoreRowModel: getCoreRowModel(),
    // getPaginationRowModel: getPaginationRowModel(),
    // onPaginationChange: setPagination,
    // state: { pagination },
    manualPagination: true,
    manualSorting: true,
    pageCount: Math.ceil(summary?.totalRows / pagination?.pageSize),
    rowCount: summary?.totalRows,
    onSortingChange: setSorting,
    state: { sorting },
  });

  const handleDownloadCsv = useCallback(async (projectId: string) => {
    await downloadProjectResults(projectId);

    toast('Downloaded CSV', {
      description: 'Downloaded the requested CSV',
      duration: 5000,
    });
  }, []);

  useEffect(() => {
    // setMetabaseUrl(undefined);

    if (projectResult.isSuccess) {
      // default payload

      let payload: MetabaseEmbedPayload = {
        resource: { dashboard: 2 },
        params: {
          project_id: [projectId],
        },
      };
      // check if the workspace has the configuration to embed the payload
      if (
        ws?.workspace &&
        ws?.workspace?.configurations &&
        ws?.workspace?.configurations?.metabase_embed_payload
      ) {
        payload = {
          resource: {
            dashboard:
              ws.workspace.configurations.metabase_embed_payload?.resource
                ?.dashboard,
          },
          params: {
            project_id: [projectId],
          },
        };
      }

      const secret = new TextEncoder().encode(
        import.meta.env.VITE_METABASE_SECRET,
      );
      const alg = 'HS256';

      new jose.SignJWT(payload)
        .setProtectedHeader({ alg })
        .setIssuedAt()
        .setIssuer('urn:example:issuer')
        .setAudience('urn:example:audience')
        .setExpirationTime('24h')
        .sign(secret)
        .then(token => {
          const iframeUrl = `https://metabase.tennisfinance.com/embed/dashboard/${token}#titled=false`;
          setMetabaseUrl(iframeUrl);
        })
        .catch(e => console.log(e));
    } else {
      setMetabaseUrl(undefined);
    }
  }, [projectResult]);

  const handleClickOutside = useCallback((event: any) => {
    // Check if the click happened outside the column dropdown and the button
    if (
      !document.getElementById('columnDropdown')?.contains(event.target) &&
      !document
        .getElementById('columnToggleDropdownButton')
        ?.contains(event.target)
    ) {
      setColumnDropdownOpen(false);
    }

    // Check if the click happened outside the download dropdown and the button
    if (
      !document.getElementById('dropdown')?.contains(event.target) &&
      !document.getElementById('dropdownDefaultButton')?.contains(event.target)
    ) {
      setDownloadDropdownOpen(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [handleClickOutside]);

  const handleDownload = (format: string) => {
    if (visibleColumns.length === 0) {
      alert('No columns selected for download');
      return;
    }

    const data = projectResult.data?.data ?? [];
    const filteredData = data.map((row: any) => {
      const newRow: any = {};
      visibleColumns.forEach(column => {
        newRow[column] = row[column];
      });
      return newRow;
    });

    const ws = XLSX.utils.json_to_sheet(filteredData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');

    if (format === 'csv') {
      const csv = XLSX.utils.sheet_to_csv(ws);
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.setAttribute('href', url);
      a.setAttribute('download', `${pr?.name}-data.csv`);
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } else if (format === 'xlsx') {
      const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([wbout], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.setAttribute('href', url);
      a.setAttribute('download', `${pr?.name}-data.xlsx`);
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    }
  };

  const handleColumnToggle = (column: any) => {
    setVisibleColumns(prevState => {
      if (prevState.includes(column)) {
        return prevState.filter(col => col !== column);
      } else {
        return [...prevState, column];
      }
    });
  };

  const toggleExpanded = (cellId: string) => {
    setExpandedCells(prevState => ({
      ...prevState,
      [cellId]: !prevState[cellId],
    }));
  };

  const isValidURL = (string: string): boolean => {
    const urlPattern = new RegExp(
      '^(https?:\\/\\/)' + // protocol
      '((([a-zA-Z\\d]([a-zA-Z\\d-]*[a-zA-Z\\d])*)\\.?)+[a-zA-Z]{2,}|' + // domain name
      '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
      '(\\:\\d+)?(\\/[-a-zA-Z\\d%_.~+]*)*' + // port and path
      '(\\?[;&a-zA-Z\\d%_.~+=-]*)?' + // query string
      '(\\#[-a-zA-Z\\d_]*)?$', // fragment locator
      'i',
    );

    return !!urlPattern.test(string);
  };

  const isLongContent = (content: any) => {
    return typeof content === 'string' && content.split(' ').length > 60;
  };

  useEffect(() => {
    projectResult?.refetch();
  }, [pagination.pageIndex, pagination.pageSize, sorting]);

  const handleSmartAnalyzeClick = async () => {
    $router.open(`/smartanalyze?projectId=${projectId}`);
    // setAnalyzingCsv(true);
    // try {
    //   // Call the function to send the download link to SmartAnalyzer
    //   const result = await smartAnalyzeCsv(projectId);
    //   setSmartAnalyzeCsvAnalyzationResult(result?.analyzation);
    //   smartAnalyzeCsvAnalyzationRef?.current?.scrollTo({
    //     top: document.documentElement.scrollHeight,
    //     behavior: 'smooth',
    //   });
    //   // window.scrollTo({
    //   //   top: document.documentElement.scrollHeight,
    //   //   behavior: 'smooth',
    //   // });
    //   toast('Data analyzed successfully!', {
    //     duration: 5000,
    //   });
    // } catch (error) {
    //   console.error('Error sending data to SmartAnalyzer:', error); // Log the error for debugging
    //   // Notify the user of the error
    //   toast('Failed to send data to SmartAnalyzer', {
    //     duration: 5000,
    //   });
    // }
    // setAnalyzingCsv(false);
  };

  const MarkdownText: React.FC<MarkdownTextProps> = ({ text }) => {
    // Function to convert markdown-like text to HTML
    const convertToHTML = (text: string): string => {
      // Replace '**Header**' with HTML <h1> or <h2> tags
      const formattedText = text
        .replace(/\*\*(.*?)\*\*/g, (_, header) => `<h2>${header}</h2>`) // Change <h2> to <h1> for main headers if needed
        .replace(/\n/g, '<br/>'); // Replace line breaks with <br> tags

      return formattedText;
    };

    // Convert text to HTML
    const htmlText = convertToHTML(text);

    return <div dangerouslySetInnerHTML={{ __html: htmlText }} />;
  };

  return (
    <div className="p-6">
      {/* Breadcrumb */}
      <h3 className="m-0 flex-1 p-0">
        <Breadcrumb pages={pages} />
      </h3>
      {/* Page Header */}
      <div className="mt-2 flex items-center justify-between space-y-2">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">
            {pr?.name || 'Project details'}
          </h2>
          {pr?.createdAtUtc && (
            <p className="text-muted-foreground">
              Created on{' '}
              <time dateTime={pr.createdAtUtc}>
                {new Date(pr.createdAtUtc).toLocaleDateString()}
              </time>
            </p>
          )}
        </div>
      </div>
      {/* Project detail */}
      <div className="mt-4">
        {projectResult.isLoading && (
          <div className="flex items-center justify-center p-10">
            <Loader2 className="h-4 w-4 animate-spin" />
          </div>
        )}

        {projectResult.isError && (
          <p className="p-10 text-center text-red-500">
            Error loading project result
          </p>
        )}

        {projectResult.isSuccess && (
          <>
            <div className={cn("relative mt-4 rounded-lg border border-gray-200 bg-white p-4 shadow-md")}>
              {/* Header Section */}
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 lg:items-center">
                {/* Left Group: Total Rows and Search Form */}
                <div className="flex flex-col space-y-4 lg:flex-row lg:items-center lg:space-y-0 lg:space-x-4">
                  {/* Total Rows */}
                  <h2 className="text-lg font-bold text-gray-800">
                    Total Rows: {summary.totalRows}
                  </h2>

                  {/* Search Form */}
                  <form
                    className="flex w-full items-center space-x-2 lg:w-auto"
                    onSubmit={(e) => {
                      e.preventDefault();
                      projectResult.refetch();
                    }}
                  >
                    <Input
                      type="text"
                      placeholder="Search anything"
                      className="flex-grow rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-300"
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <Button
                      type="submit"
                      size="icon"
                      variant="outline"
                      className="flex-shrink-0 rounded-lg border border-gray-300 bg-white p-2 text-gray-600 hover:bg-gray-100 focus:ring-2 focus:ring-blue-300"
                    >
                      <Search className="h-5 w-5" />
                    </Button>
                  </form>
                </div>

                {/* Right Group: Action Buttons */}
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:flex lg:items-center lg:justify-end lg:space-x-3">
                  {/* Smart Analyze Button */}
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span style={{ display: 'inline-block' }}>
                          <Button
                            className="mr-2"
                            onClick={async () => {
                              await handleSmartAnalyzeClick();
                              setOpen(false);
                            }}
                            // disabled={summary.totalRows > 1000}
                          >
                            <Sparkles className="mr-1 h-3 w-3" />
                            {analyzingCsv ? 'Processing...' : 'Smart Analyze'}
                          </Button>
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        {/* {summary.totalRows > 1000
                          ? "Limit is 1000 rows. Please filter and reupload file to smart analyze page."
                          : "Analyze this data with AI"} */}
                        Analyze this data with AI
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>

                  {/* Show/Hide Columns Button */}
                  <div className="relative w-full lg:w-auto">
                    <Button
                      id="columnToggleDropdownButton"
                      className="flex w-full items-center justify-center rounded-lg bg-green-500 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-300 lg:w-auto"
                      onClick={() => setColumnDropdownOpen(!isColumnDropdownOpen)}
                    >
                      Show/Hide Columns
                    </Button>
                    {isColumnDropdownOpen && (
                      <div
                        id="columnDropdown"
                        className="absolute right-0 z-20 mt-2 w-full rounded-lg border border-gray-300 bg-white shadow-lg lg:w-64"
                      >
                        <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-4 py-3">
                          <button
                            className="text-sm text-blue-500 hover:underline focus:outline-none"
                            onClick={() => setVisibleColumns(projectResult.data.columns)}
                          >
                            Select All
                          </button>
                          <button
                            className="text-sm text-blue-500 hover:underline focus:outline-none"
                            onClick={() => setVisibleColumns([])}
                          >
                            Deselect All
                          </button>
                        </div>
                        <ul className="max-h-60 space-y-1 overflow-y-auto py-2 text-sm text-gray-700">
                          {projectResult?.data?.columns?.map((column: any) => (
                            <li key={column} className="flex items-center px-4 py-2 hover:bg-gray-50">
                              <Checkbox
                                checked={visibleColumns.includes(column)}
                                onCheckedChange={() => handleColumnToggle(column)}
                                className="h-4 w-4 text-green-500 focus:ring-2 focus:ring-green-400"
                              />
                              <span className="ml-2">{column}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>



                  {/* Download Button */}
                  <div className="relative w-full lg:w-auto">
                    <Button
                      id="dropdownDefaultButton"
                      className="flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-500 hover:bg-blue-600 rounded-lg shadow w-full lg:w-auto"
                      onClick={() => setDownloadDropdownOpen(!isDownloadDropdownOpen)}
                    >
                      Download
                      <svg
                        className="ml-2 h-4 w-4"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 10 6"
                      >
                        <path
                          stroke="currentColor"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M1 1l4 4 4-4"
                        />
                      </svg>
                    </Button>
                    {isDownloadDropdownOpen && (
                      <div
                        id="dropdown"
                        className="absolute right-0 z-20 mt-2 w-full bg-white border border-gray-300 rounded-lg shadow-lg"
                      >
                        <ul className="py-2 text-sm text-gray-700">
                          <li>
                            <a
                              href="#"
                              onClick={() => handleDownloadCsv(projectId)}
                              className="block px-4 py-2 hover:bg-gray-100 whitespace-nowrap"
                            >
                              Export to CSV
                            </a>
                          </li>
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>






            {/* Instructions */}
            <p className="mt-4 text-sm text-gray-600">
              * To report an incorrect value, click on the blue icon beside the value.
            </p>
            <div
              className={cn(
                'mb-4 mt-5 overflow-x-auto rounded-md border shadow-sm',
                sidebarCollapsed
                  ? 'max-w-[calc(100vw-12rem)]'
                  : 'max-w-[calc(100vw-24rem)]',
                projectResult?.isFetching && 'animate-pulse',
              )}
            >


              <Table >
                <TableHeader>
                  {table.getHeaderGroups().map(headerGroup => (
                    <TableRow key={headerGroup.id}>
                      {headerGroup.headers.map(header => (
                        <TableHead key={header.id}>
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                        </TableHead>
                      ))}
                    </TableRow>
                  ))}
                </TableHeader>
                <TableBody>
                  {table.getRowModel().rows?.length ? (
                    table.getRowModel().rows.map(row => (
                      <TableRow
                        key={row.id}
                        data-state={row.getIsSelected() && 'selected'}
                      >
                        {row.getVisibleCells().map(cell => (
                          <TableCell key={cell.id}>
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext(),
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={columns.length}
                        className="h-24 text-center"
                      >
                        No results.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>

            <div className="mb-6 flex flex-1 items-center justify-end gap-2 p-2">
              <p>Page size:</p>
              <select
                value={pagination?.pageSize}
                onChange={e =>
                  setPagination(prev => ({
                    pageSize: parseInt(e.target.value),
                    pageIndex: 1,
                  }))
                }
                className={cn('rounded border-gray-300 p-1', 'dark:text-black')}
              >
                {[10, 20, 30, 40, 50].map(pageSize => (
                  <option key={pageSize} value={pageSize}>
                    {pageSize}
                  </option>
                ))}
              </select>

              <button
                className="rounded border-gray-300 p-1"
                onClick={() =>
                  setPagination(prev => ({ ...prev, pageIndex: 1 }))
                }
                disabled={pagination?.pageIndex === 1}
              >
                <ChevronFirst />
              </button>
              <button
                className="rounded border-gray-300 p-1"
                onClick={() =>
                  setPagination(prev => ({
                    ...prev,
                    pageIndex: prev.pageIndex - 1,
                  }))
                }
                disabled={pagination?.pageIndex === 1}
              >
                <ChevronLeft />
              </button>
              <span className="flex items-center gap-1">
                <div>Page</div>
                <strong>
                  {pagination?.pageIndex} of {table.options.pageCount}
                </strong>
              </span>
              <button
                className="rounded border-gray-300 p-1"
                onClick={() => {
                  setPagination(prev => ({
                    ...prev,
                    pageIndex: prev.pageIndex + 1,
                  }));
                  // console.log('next page', table.nextPage());
                }}
                disabled={pagination?.pageIndex === table.options.pageCount}
              >
                <ChevronRight />
              </button>
              <button
                className="rounded border-gray-300 p-1"
                onClick={() =>
                  setPagination(prev => ({
                    ...prev,
                    pageIndex: table.options.pageCount!,
                  }))
                }
                disabled={pagination?.pageIndex === table.options.pageCount}
              >
                <ChevronLast />
              </button>
            </div>
          </>
        )}
      </div >
      <iframe
        src={metabaseUrl}
        width="100%"
        height="1100"
        title="Embedded Content"
        className="rounded-lg shadow-lg"
      />
      {
        smartAnalyzeCsvAnalyzationResult &&
        typeof smartAnalyzeCsvAnalyzationResult === 'string' && (
          <div ref={smartAnalyzeCsvAnalyzationRef}>
            <Markdown>{smartAnalyzeCsvAnalyzationResult}</Markdown>
            {/* <MarkdownText text={smartAnalyzeCsvAnalyzationResult} /> */}
          </div>
        )
      }
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleReport}
        rowId={currentRowId!} // Pass the current row ID
        columnId={currentColumnId!} // Pass the current column ID
        originalValue={originalValue!} // Pass the original value
      />
      {message && <div>{message}</div>} {/* Display feedback message */}
    </div >
  );
};
